import Avatar from "@mui/material/Avatar";
import {
  ACCOUNT_STATUS_INACTIVE,
  ACCOUNT_TYPE_STAFF,
  ACCOUNT_TYPE_TOOL,
  DEPRECATED_ACCOUNT_TYPES,
  externalUser,
} from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { getInitialsForAccount, getName } from "core/model/accounts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { isPrintMode } from "core/model/utils/urls";
import {
  Careprovider,
  Careseeker,
  Event as EventType,
  GetOntologyType,
} from "core/types";
import { format, formatRelative, fromUnixTime } from "date-fns";
import {
  ACCENT_DARK_COLOR,
  CARD_BACKGROUND,
  FAB_DEFAULT_BACKGROUND,
  ICON_DARK,
  PRIMARY_DARK_COLOR,
  PROVIDER_PRIMARY_DARK_COLOR,
  TEXT_ACCENT_DARK,
  WHITE,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { border, dp, margin, padding } from "ds_legacy/materials/metrics";
import { SkeletonCircle, SkeletonRectangle } from "ds_legacy/materials/shapes";
import {
  Body,
  Caption,
  FONT_SIZE_16,
  EventChip as Tooltip,
} from "ds_legacy/materials/typography";
import { EventSpecMessage } from "dsl/atoms/EventSpecs";
import { MailIcon } from "lucide-react";
import React, { CSSProperties } from "react";
import styled from "styled-components";
import { useTranslations } from "translations";
import { TranslationComposition } from "translations/helpers";
import Translations from "translations/types";

export function TimelineEventCard({
  event,
  forFhir,
  forIsik,
  icon,
  title,
}: {
  event: EventType;
  forFhir?: boolean;
  forIsik?: boolean;
  icon?: any;
  title: string;
}) {
  const isPrint = isPrintMode();
  const { currentLocale: locale } = useDateLocale();

  if (!isPrint) {
    return (
      <HorizontalLayout
        data-testid={`event-${event.type}`}
        flexShrink={1}
        margin={margin(2, 0)}
      >
        {forFhir ? null : (
          <HorizontalLayout width={dp(45)}>
            <Avatar
              sx={{
                backgroundColor: WHITE,
                border: border({ color: ICON_DARK }),
                color: ICON_DARK,
                height: 40,
                width: 40,
              }}
            >
              {icon || (event.account && getInitialsForAccount(event.account))}
            </Avatar>
          </HorizontalLayout>
        )}
        <VerticalLayout flexShrink={1}>
          <Body>{title}</Body>
          <HorizontalLayout>
            <Caption>
              {event.timestamp &&
                (forIsik
                  ? format(fromUnixTime(event.timestamp), "PPpp", { locale })
                  : formatRelative(fromUnixTime(event.timestamp), new Date(), {
                      locale,
                    }))}
            </Caption>
          </HorizontalLayout>
        </VerticalLayout>
      </HorizontalLayout>
    );
  }

  return (
    <>
      <td>
        <Caption>
          {event.timestamp &&
            format(fromUnixTime(event.timestamp), "PPpp", { locale })}
        </Caption>
      </td>
      <td>
        <Body>{title}</Body>
      </td>
    </>
  );
}

const EventChip = styled.span<{ chipMargin?: string; chipPadding?: string }>`
  display: flex;
  align-items: center;
  border-radius: ${dp(7)};
  padding: ${({ chipPadding }) => chipPadding || padding(0.7, 1)};
  margin: ${(props) => props.chipMargin || margin(0, 1, 0, 0)};
  background-color: ${(props) => props.color || FAB_DEFAULT_BACKGROUND};
`;

export const Chip = ({
  chipMargin,
  chipPadding,
  color,
  label,
  maxCharacters,
}: {
  chipMargin?: string;
  chipPadding?: string;
  color?: string;
  label: string;
  maxCharacters?: number;
}) => (
  <EventChip chipMargin={chipMargin} chipPadding={chipPadding} color={color}>
    <Tooltip>
      {maxCharacters && label.length > maxCharacters
        ? `${label.substring(0, maxCharacters - 3)}...`
        : label}
    </Tooltip>
  </EventChip>
);

export const AccountChip = ({
  accountName,
  accountType,
}: {
  accountName?: string;
  accountType: number | undefined;
}) => {
  if (externalUser(accountType))
    return <Chip color={TEXT_ACCENT_DARK} label={accountName || "External "} />;

  switch (accountType) {
    case ACCOUNT_TYPE_STAFF:
      return <Chip color="#C51162" label={accountName || "Recare Staff"} />;
    case ACCOUNT_TYPE_TOOL:
      return <Chip color={CARD_BACKGROUND} label="Tool" />;
    default:
      if (!DEPRECATED_ACCOUNT_TYPES.includes(accountType || -1))
        console.error("Could not find account type ", accountType);
      return null;
  }
};

export function stringToHex(str: string) {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return `#${"00000".substring(0, 6 - c.length)}${c}`;
}

const WithLink = ({
  children,
  link,
}: {
  children: React.ReactNode;
  link?: string;
}) =>
  link ? (
    <a
      href={link}
      rel="noopener noreferrer"
      style={{ textDecoration: "none" }}
      target="_blank"
    >
      {children}
    </a>
  ) : (
    <>{children}</>
  );

function TimelineSpecMessage({
  eventMessage,
  textAlign,
  whiteSpace,
}: {
  eventMessage: Exclude<EventSpecMessage, string> | undefined;
  textAlign?: CSSProperties["textAlign"];
  whiteSpace?: CSSProperties["whiteSpace"];
}) {
  if (!eventMessage || typeof eventMessage === "string") return null;

  return (
    <>
      {eventMessage.title && (
        <Body margin={margin(0)} textAlign={textAlign} whiteSpace="pre-wrap">
          {eventMessage.title}
        </Body>
      )}
      {eventMessage.date && (
        <Caption
          margin={margin(0, 1.25, 0, 1.25)}
          textAlign={textAlign}
          whiteSpace={whiteSpace}
        >
          {eventMessage.date}
        </Caption>
      )}
      {eventMessage.date && eventMessage.time && (
        <Caption
          margin={margin(0, 1.25, 0, 1.25)}
          textAlign={textAlign}
          whiteSpace={whiteSpace}
        >
          {eventMessage.time}
        </Caption>
      )}
    </>
  );
}

function AcpChip({
  chips,
  event,
  eventMessages,
  Icon,
  message,
  title,
}: {
  Icon?: React.ComponentType<any>;
  chips?: React.ReactNode[];
  event: EventType;
  eventMessages?: EventSpecMessage[];
  message?: React.JSX.Element | string | null;
  title?: React.JSX.Element | string;
}) {
  const { currentLocale: locale } = useDateLocale();

  return (
    <HorizontalLayout aligned flexShrink={1} margin={margin(1, 0)}>
      {Icon ? (
        <div style={{ margin: "27px 0px 0px 0px" }}>
          <Icon
            size={FONT_SIZE_16}
            style={{ color: ICON_DARK, fontSize: dp(16) }}
          />
        </div>
      ) : undefined}
      <VerticalLayout
        flexShrink={1}
        margin={Icon ? undefined : margin(0, 0, 0, 1.7)}
        stretched
      >
        <HorizontalLayout aligned>
          <Caption>
            {event.timestamp &&
              formatRelative(fromUnixTime(event.timestamp), new Date(), {
                locale,
              })}
          </Caption>
          <HorizontalLayout justify="flex-end" stretched>
            {chips}
          </HorizontalLayout>
        </HorizontalLayout>
        {title && <Body>{title}</Body>}
        {message && <Caption whiteSpace="normal">{message}</Caption>}
        {eventMessages?.map((msg, idx) => (
          <TimelineSpecMessage key={idx.toString()} eventMessage={msg} />
        ))}
      </VerticalLayout>
    </HorizontalLayout>
  );
}

export function TimelineAuctionEventCard({
  careprovider,
  careseeker,
  event,
  eventMessages,
  message,
  title,
}: {
  careprovider?: Careprovider;
  careseeker?: Careseeker;
  event: EventType;
  eventMessages?: EventSpecMessage[];
  message?: React.JSX.Element | string | null;
  title: React.JSX.Element | string;
}) {
  const getOntology = useGetOntology();
  const getEventMessage = () => {
    if (message) return message;

    if (!event.context?.message) return "";

    if (event.context.message_iv != null) return "";

    return event.context.message;
  };

  const eventMessage = getEventMessage();

  const chips = [];

  if (careseeker?.name)
    chips.push(
      <Chip
        key="careseeker_name"
        color={PRIMARY_DARK_COLOR}
        label={careseeker.name}
        maxCharacters={70}
      />,
    );
  else if (careprovider?.name) {
    chips.push(
      <Chip
        key="careprovider_name"
        color={PROVIDER_PRIMARY_DARK_COLOR}
        label={careprovider.name}
        maxCharacters={70}
      />,
    );
  }

  if (event.account?.first_name && getName(event.account, getOntology))
    chips.push(
      <Chip
        key="account_name"
        color={ACCENT_DARK_COLOR}
        label={getName(event.account, getOntology)}
      />,
    );

  if (event.context?.user_id) {
    if (event.context?.request_link)
      chips.push(
        <WithLink key="user_id" link={event.context.request_link}>
          <Chip label={event.context.user_id} />
        </WithLink>,
      );
    // For the patient event - no request link.
    else if (event.patient_id)
      chips.push(
        <WithLink
          key="user_id"
          link={`https://app.recaresolutions.com/app/clinic/dashboard/patient/${event.patient_id}`}
        >
          <Chip label={event.context.user_id} />
        </WithLink>,
      );
  }

  return (
    <AcpChip
      chips={chips}
      event={event}
      eventMessages={eventMessages}
      message={eventMessage}
      title={title}
    />
  );
}

export function TimelineEventCardCareprovider({
  event,
  eventMessages,
  Icon,
  message,
  title,
}: {
  Icon?: any;
  event: EventType;
  eventMessages?: EventSpecMessage[];
  message?: React.JSX.Element | string | null;
  title: React.JSX.Element | string;
}) {
  const getOntology = useGetOntology();

  const chips = [];

  if (event.account)
    chips.push(
      <AccountChip
        key="account"
        accountName={getName(event.account, getOntology)}
        accountType={event?.account?.account_type}
      />,
    );

  if (event.careseeker?.name)
    chips.push(
      <Chip
        key="careseeker_name"
        color={TEXT_ACCENT_DARK}
        label={event.careseeker.name}
      />,
    );
  return (
    <AcpChip
      chips={chips}
      event={event}
      eventMessages={eventMessages}
      Icon={Icon}
      message={message}
      title={title}
    />
  );
}

const SkeletonElement = styled.div`
  display: flex;
  height: 77px;
  flex-shrink: 1;
`;

export function SkeletonCard() {
  return (
    <SkeletonElement>
      <SkeletonCircle />
      <VerticalLayout flexShrink={1} margin={margin(1, 2)}>
        <SkeletonRectangle height="10px" width="80px" />
        <HorizontalLayout margin={margin(1, 0)}>
          <SkeletonRectangle height="10px" width="30px" />
        </HorizontalLayout>
      </VerticalLayout>
    </SkeletonElement>
  );
}

const StatusComparison = ({
  event,
  target,
  text,
}: {
  event: EventType;
  target:
    | "accountStatus"
    | "careproviderStatus"
    | "careseekerStatus"
    | "onboardingStatus";
  text: (status: { newStatus: string; prevStatus: string }) => string;
}) => {
  const getOntology = useGetOntology();
  const getStatus = (status: number) =>
    getOntology({ key: status, type: target });

  const prevStatus =
    (event.context?.previous_status != null &&
      getStatus(event.context.previous_status)) ||
    "";
  const newStatus =
    (event.context?.new_status != null &&
      getStatus(event.context.new_status)) ||
    "";

  return (
    <TranslationComposition
      translations={text({ newStatus, prevStatus })}
      withOptions
    >
      {([before, previous, to, next, after]) => (
        <>
          {before}
          <b>{previous}</b>
          {to}
          <b>{next}</b>
          {after}
        </>
      )}
    </TranslationComposition>
  );
};

export const CareproviderStatusChanged = ({
  event,
  translations,
}: {
  event: EventType;
  translations: Translations;
}) => (
  <TimelineEventCardCareprovider
    event={event}
    title={
      <StatusComparison
        event={event}
        target="careproviderStatus"
        text={translations.timeline.careproviderStatusChanged}
      />
    }
  />
);

export const AccountStatusChanged = ({
  event,
  translations,
}: {
  event: EventType;
  translations: Translations;
}) => {
  let title: string | React.JSX.Element = "";

  if (event.context?.new_status === ACCOUNT_STATUS_INACTIVE) {
    title = translations.acp.accountTimeline.accountDeactivated;
  } else if (event.context?.previous_status === ACCOUNT_STATUS_INACTIVE) {
    title = translations.acp.accountTimeline.accountReactivated;
  } else {
    title = (
      <StatusComparison
        event={event}
        target="accountStatus"
        text={translations.timeline.statusChanged}
      />
    );
  }

  return <TimelineEventCardCareprovider event={event} title={title} />;
};

export const OnboardingStatusChanged = ({
  event,
  translations,
}: {
  event: EventType;
  translations: Translations;
}) => (
  <TimelineEventCardCareprovider
    event={event}
    message={event.context?.onboarding_comment}
    title={
      <StatusComparison
        event={event}
        target="onboardingStatus"
        text={translations.timeline.careproviderOnboardingStatusChanged}
      />
    }
  />
);

export const CareseekerStatusChanged = ({
  event,
  translations,
}: {
  event: EventType;
  translations: Translations;
}) => (
  <TimelineAuctionEventCard
    event={event}
    title={
      <StatusComparison
        event={event}
        target="careseekerStatus"
        text={translations.timeline.statusChanged}
      />
    }
  />
);

export const EmailSent = ({
  event,
  forCareprovider,
}: {
  event: EventType;
  forCareprovider?: boolean;
}) => {
  const getOntology = useGetOntology();
  if (!forCareprovider || !event.context) return null;

  const emailType = getOntology({
    key: event.context.mail_type,
    type: ONTOLOGIES.EMAIL_TYPE,
  });

  const recipients = event.context?.recipients?.join(", ");

  return (
    <AcpChip
      chips={[
        <Chip
          key="emailtype"
          label={emailType || (event.context.mail_type || "").toString()}
        />,
      ]}
      event={event}
      Icon={MailIcon}
      message={recipients}
    />
  );
};

const getRoles = (event: EventType, getOntology: GetOntologyType) =>
  event.context?.new_roles
    ?.map((key) => getOntology({ key, type: "accountRoleShort" }))
    .join(", ") || "";

export function EventAccountPermissions({ event }: { event: EventType }) {
  const translations = useTranslations();
  const getOntology = useGetOntology();

  const destination = event.subject_account
    ? `${event.subject_account.first_name || ""} ${
        event.subject_account.last_name || ""
      } - `
    : "";

  if (event.context?.careprovider_name)
    return (
      <TimelineEventCardCareprovider
        event={event}
        title={`${destination}${translations.acp.accountTimeline.permissionsUpdated(
          {
            resource: `${event.context.careprovider_name} ${getRoles(
              event,
              getOntology,
            )}`,
          },
        )}`}
      />
    );
  if (event.careseeker?.name)
    return (
      <TimelineEventCardCareprovider
        event={event}
        title={`${destination}${translations.acp.accountTimeline.permissionsUpdated(
          {
            resource: `${event.careseeker.name} ${getRoles(
              event,
              getOntology,
            )}`,
          },
        )}`}
      />
    );

  return (
    <TimelineEventCardCareprovider
      event={event}
      title={`${destination}${translations.acp.accountTimeline.permissionsUpdated(
        {
          resource: getRoles(event, getOntology),
        },
      )}`}
    />
  );
}
